import { isEmpty, isNil } from 'lodash-es';
import moment from 'moment';

import { IPublisher, IReport, transformReportToAPI } from '~/src/dash/api/console-api';
import { ECurrencyType } from '~/src/dash/api/exchange-rates';
import {
  addDomainAndTokenToUrl,
  createAnalyticsUrl,
  createUrl,
  defaultRestGetArgs,
  ResponseType,
  restGet,
  restPatch,
  restPost,
} from '~/src/dash/api/rest';
import { EBool } from '~/src/dash/api/types/bool';
import { isIASReportType } from '~/src/dash/apiV2/reporting/reportTypes';
import { formatDateEndDay, formatDateStartDay } from '~/src/dash/utils/date';
import { deviceTargetingMap, getReportGroupType } from '~/src/dash/utils/graph';
import { encodeParams } from '~/src/dash/utils/params';
import { EReportPriority } from '~/src/dash/views/reporting/common';

// create a new report
export async function createReport(
  publisherID: string,
  params: { [key: string]: any },
): Promise<any> {
  const report = { report: transformReportToAPI(params) } as { [key: string]: any };
  const reqUrl = `${addDomainAndTokenToUrl(`/report`)}&publisher_id=${publisherID}&${encodeParams(
    report,
  )}`;
  try {
    const response = await restPost<any>(reqUrl, {});
    return [response.parsedBody.report, response.parsedBody.saved_as_quick_report_type];
  } catch (err) {
    throw err;
  }
}

// update an existing report
export async function updateReport(publisherID: string, params: { [key: string]: any }) {
  const report = { report: transformReportToAPI(params) } as { [key: string]: any };
  const reqUrl = `${addDomainAndTokenToUrl(`/report`)}&publisher_id=${publisherID}&report_id=${
    params.ID
  }&${encodeParams(report)}`;
  try {
    const response = await restPatch<any>(reqUrl, {});
    return [response.parsedBody.report, response.parsedBody.saved_as_quick_report_type];
  } catch (err) {
    throw err;
  }
}

// schedule a report'sa data to be delivered via email or s3
export async function scheduleReport(publisherID: string, params: { [key: string]: any }) {
  const reqUrl = `${addDomainAndTokenToUrl(
    `/schedule_report`,
  )}&publisher_id=${publisherID}&report_id=${params.ID}&${encodeParams(params)}`;
  try {
    const response = await restPatch<any>(reqUrl, {});
    return response.parsedBody.report;
  } catch (err) {
    throw err;
  }
}

interface IReportAPIParams {
  IsPartialAdBreak: string;
  ReportFilterConfig: {
    AdvertiserIDs: string;
    BidderActiveOnly: boolean;
    BidderLabelIDs: string;
    ChannelActiveOnly: boolean;
    ChannelLabelIDs: string;
    CountryGroupIDs: string;
    Livestream: number | null;
    OrderIDs: string;
  };
  adomains: string;
  an_id: string; // IAS Reports only
  bidder_group_ids: string;
  bidder_ids: string;
  bidder_tier_filter: string;
  biddercodes: string;
  campaign_ids: string;
  channel_group_ids: string;
  content_channel_filter: string;
  content_network_filter: string;
  countries: string;
  currencydisplayed: ECurrencyType;
  deal_ids: string;
  destination_ids: string;
  device_subtypes: string;
  deviceenvs: string;
  devicetypes: string;
  dmas: string;
  end_date: string;
  exchangerate: number;
  graph_id: string;
  groupbyoptions: string;
  grouping: number;
  ias_body: IIASEmailReportBody; // IAS Reports only
  inventory_route_ids: string;
  is_bidder_ids_included: number;
  is_site_ids_included: number;
  line_item_aliases: string;
  metrics: string;
  network_ids: string;
  orig_content_channel_filter: string;
  orig_content_network_filter: string;
  order_aliases: string;
  period: string;
  priority: string;
  publisher_id: string;
  recipients: string;
  report_duration_days: number;
  report_id: string;
  report_limit: string;
  report_name: string;
  report_order_by: string;
  reportgrouptype: number;
  reporttype: string;
  segment_ids: string;
  should_upload_to_external_s3: EBool;
  site_ids: string;
  siteids: string;
  start_date: string;
  states: string;
  stream_types: string;
  subject: string;
  timezone: string;
  venue_ids: string;
}

export const toReportAPIParams = (
  exchangeRate: number,
  graphID: string | number,
  priority: EReportPriority,
  publisher: IPublisher,
  recipients: string[],
  report: IReport,
): IReportAPIParams => {
  // helper functions to format params
  const arrToStr = (arr: Array<any>) =>
    Array.isArray(arr) ? arr.filter((item) => item !== '' || !isNil(item)).join(',') : '';

  const getGrouping = (groupByOptions: string[]) => {
    if (groupByOptions.includes('hour')) {
      return 0;
    } else if (groupByOptions.includes('weeks')) {
      return 2;
    } else if (groupByOptions.includes('month')) {
      return 3;
    } else if (groupByOptions.includes('week_of_year')) {
      return 4;
    } else {
      return 1;
    }
  };

  const groupbyoptions = arrToStr(report.GroupByOptions);
  return {
    IsPartialAdBreak: report.IsPartialAdBreak || '',
    ReportFilterConfig: {
      AdvertiserIDs: arrToStr(report.ReportFilterConfig?.AdvertiserIDs),
      BidderActiveOnly: report.ReportFilterConfig?.BidderActiveOnly || false,
      BidderLabelIDs: arrToStr(report.ReportFilterConfig?.BidderLabelIDs),
      ChannelActiveOnly: report.ReportFilterConfig?.ChannelActiveOnly || false,
      ChannelLabelIDs: arrToStr(report.ReportFilterConfig?.ChannelLabelIDs),
      CountryGroupIDs: arrToStr(report.ReportFilterConfig?.CountryGroupIDs),
      Livestream: report.ReportFilterConfig?.Livestream || null,
      OrderIDs: arrToStr(report.ReportFilterConfig?.OrderIDs),
    },
    adomains: report.Adomains,
    an_id: arrToStr(report.IASAnID), // IAS Reports only
    bidder_group_ids: arrToStr(report.BidderGroupsIDs),
    bidder_ids: arrToStr(report.BidderIDs),
    bidder_tier_filter: report.BidderTierFilter,
    biddercodes: arrToStr(report.BidderCodes),
    campaign_ids: arrToStr(report.CampaignIDs),
    channel_group_ids: arrToStr(report.ChannelGroupIDS),
    content_channel_filter: arrToStr(report.ContentChannelFilter),
    content_network_filter: arrToStr(report.ContentNetworkFilter),
    countries: arrToStr(report.Countries),
    currencydisplayed: report.CurrencyDisplayed,
    deal_ids: report.DealIDs,
    destination_ids: arrToStr(report.DestinationIDs),
    device_subtypes: arrToStr(report.DeviceSubtypes),
    deviceenvs: arrToStr(report.DeviceEnvs?.map((item) => deviceTargetingMap[item])),
    devicetypes: arrToStr(
      report.DeviceTypes?.map((devices: string) => String(devices).split('|')) // IAS reports have devicetype in this format "111|112"
        ?.flat(),
    ),
    dmas: report.DMAs || '',
    end_date: formatDateEndDay(report.EndDate),
    exchangerate: exchangeRate,
    graph_id: String(graphID),
    groupbyoptions,
    grouping: getGrouping(report.GroupByOptions),
    ias_body: isIASReportType(Number(report.ReportType))
      ? formatIASReportRequestBody(
          graphID,
          priority,
          publisher,
          !isEmpty(recipients) ? recipients : report.Recipients,
          report,
        )
      : ({} as IIASEmailReportBody), // IAS Reports only
    inventory_route_ids: arrToStr(report.InventoryRouteIDs),
    is_bidder_ids_included: report.IsBidderIDsIncluded || EBool.EBool_TRUE,
    is_site_ids_included: report.IsSiteIDsIncluded || EBool.EBool_TRUE,
    line_item_aliases: arrToStr(report.LineItemAliases),
    metrics: arrToStr(report.Metrics),
    network_ids: arrToStr(report.NetworkIDs),
    orig_content_channel_filter: arrToStr(report.OrigContentChannelFilter),
    orig_content_network_filter: arrToStr(report.OrigContentNetworkFilter),
    order_aliases: arrToStr(report.OrderAliases),
    period: report.Period,
    priority: priority === EReportPriority.Unknown ? '' : String(priority),
    publisher_id: publisher.ID,
    recipients: !isEmpty(recipients) ? arrToStr(recipients) : arrToStr(report.Recipients),
    report_duration_days: moment(report.EndDate).diff(moment(report.StartDate), 'days'),
    report_id: report.ID || '0',
    report_limit: report.ReportLimit || '0',
    report_name: report.Name,
    report_order_by: report.ReportOrderBy,
    reportgrouptype: getReportGroupType(groupbyoptions),
    reporttype: report.ReportType,
    segment_ids: arrToStr(report.SegmentIDs),
    should_upload_to_external_s3: report.ShouldUploadToExternalS3,
    site_ids: arrToStr(report.SiteIDs),
    siteids: arrToStr(report.SiteIDs),
    start_date: formatDateStartDay(report.StartDate),
    states: arrToStr(report.States),
    stream_types: arrToStr(report.StreamTypes),
    subject: report.Subject,
    timezone: report.Timezone,
    venue_ids: report.VenueIDs,
  };
};

// email a report's data
export async function emailReport(params: { [key: string]: any }) {
  const reqUrl = `${createAnalyticsUrl('discovery/email_report')}&${encodeParams(params)}`;

  try {
    const response = await restPost<any>(reqUrl, {});
    return response.parsedBody.report;
  } catch (err) {
    throw err;
  }
}

export interface IIASEmailReportBody {
  AnIDs: number[];
  CreativeIDs?: string[];
  Debug?: boolean;
  DeviceTypes: number[];
  EndDate: string;
  GraphID: string;
  GroupBys?: string[];
  GroupingNumber: number;
  IsBidderIDsIncluded: EBool;
  LineItemIDs?: string[];
  Metrics?: string[];
  OrderIDs?: string[];
  Priority?: string;
  PublicaBidderGroupIDs: number[];
  PublicaBidderIDs: number[];
  PublicaBidderTypes: string[];
  PublicaChannelIDs: number[];
  Recipients?: string[];
  ReportLimit?: number;
  ReportName?: string;
  StartDate: string;
  TeamID: string;
  Timezone: string;
}

export const formatIASReportRequestBody = (
  graphID: string | number,
  priority: EReportPriority,
  publisher: IPublisher,
  recipients: string[],
  report: IReport,
): IIASEmailReportBody => {
  return {
    AnIDs: !isEmpty(report.IASAnID)
      ? report.IASAnID?.map((v: string) => parseInt(v, 10))
      : [Number(publisher.IASAnID)],
    CreativeIDs: [], // blank until we add ability for filter dropdowns
    Debug: false,
    DeviceTypes:
      report.DeviceTypes?.map((devices: string) => String(devices).split('|'))
        ?.flat()
        ?.map((v: string) => parseInt(v, 10)) || [],
    EndDate: formatDateEndDay(report.EndDate),
    GraphID: `graph${graphID}.sql`,
    GroupBys: report.GroupByOptions.concat(
      report.GroupByOptions.includes('month') || report.GroupByOptions.includes('day')
        ? ['date']
        : [],
    ),
    GroupingNumber: report.GroupByOptions.includes('month') ? 3 : 1,
    IsBidderIDsIncluded: report.IsBidderIDsIncluded || EBool.EBool_TRUE,
    LineItemIDs: [], // blank until we add ability for filter dropdowns,
    Metrics: report.Metrics,
    OrderIDs: [], // blank until we add ability for filter dropdowns,
    Priority: String(priority),
    PublicaBidderGroupIDs: report.BidderGroupsIDs.map((id) => Number(id)),
    PublicaBidderIDs: report.BidderIDs.map((id) => Number(id)),
    PublicaBidderTypes: report.BidderCodes.map(String),
    PublicaChannelIDs: report.SiteIDs.map((id) => Number(id)),
    Recipients: !isEmpty(recipients) ? recipients : report.Recipients,
    ReportLimit: Number(report.ReportLimit),
    ReportName: report.Name,
    StartDate: formatDateStartDay(report.StartDate),
    TeamID: '15661',
    Timezone: report.Timezone,
  };
};

// email an IAS report's data
export async function emailIASReport(
  queryParams: { [key: string]: any },
  body: IIASEmailReportBody,
): Promise<any> {
  const reqUrl = addDomainAndTokenToUrl('/email_ias_report') + `&${encodeParams(queryParams)}`;
  try {
    const response = await restPost<any>(reqUrl, body);
    return response.parsedBody;
  } catch (err) {
    throw err;
  }
}

// download a report's data
export async function downloadReportData(params: { [key: string]: any }): Promise<any> {
  const reqUrl = `${createAnalyticsUrl('discovery/download_report')}&${encodeParams(params)}`;

  try {
    const response = await restGet<any>(reqUrl, defaultRestGetArgs, ResponseType.blob);
    return response.parsedBody;
  } catch (err) {
    throw err;
  }
}

// download an IAS report's data
export async function downloadIASReportData(params: { [key: string]: any }): Promise<any> {
  const reqUrl = `${createUrl('/download_ias_report')}&${encodeParams(params)}`;

  try {
    const response = await restGet<any>(reqUrl, defaultRestGetArgs, ResponseType.blob);
    return response.parsedBody;
  } catch (err) {
    throw err;
  }
}
